import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useData } from '../../contexts/data-context/use-data';
import { useUserInfo } from '../../contexts/user-context/use-user-info';
import { Button } from '../../design-system/button/button';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { LoadingSection } from '../../design-system/loading/loading';
import { Stack } from '../../design-system/stack/stack';
import { CardView } from '../../types/entity-types';
import { isBankingAccount } from '../../utils/accounts/is-banking-account';
import { getCountryCurrency } from '../../utils/get-country-currency';
import { CardCreationDrawer } from './components/card-creation-drawer/card-creation-drawer';
import { CancelCardDialog } from './components/card-details-drawer/cancel-card-dialog';
import { CardDetailsDrawer } from './components/card-details-drawer/card-details-drawer';
import { EditCardNicknameDialog } from './components/card-details-drawer/edit-card-nickname-dialog';
import { CardsTable } from './components/data-table-components/cards-table';
import { CardsEmptyState } from './components/empty-states/cards-empty-state';
import { MissingSeapointAccount } from './components/empty-states/missing-seapoint-account';
import { useCancelCard } from './hooks/use-cancel-card';
import { useUpdateCardDisplayName } from './hooks/use-update-card-display-name';
import { parseUserName } from './utils/parse-user-name';

export const CardsPage = () => {
  const { data, isLoading, error } = useData();
  const { authenticatedInfo } = useUserInfo();
  const { updateDisplayName, isUpdating } = useUpdateCardDisplayName();
  const { cancelCard, isCancelling } = useCancelCard();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [editingCard, setEditingCard] = useState<CardView | null>(null);
  const [cancellingCard, setCancellingCard] = useState<CardView | null>(null);

  const selectedCard = data ? (data.cards.find((c) => c.id === selectedCardId) ?? null) : null;

  if (isLoading) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data) {
    return <ErrorSection error={error} />;
  }

  // Get the organization's country to determine allowed currency for cards
  // UK organizations can only create cards for GBP accounts
  // EU organizations (IE, DE) can only create cards for EUR accounts
  const organizationCountry = data.companies[0]?.legalEntity.country;
  const allowedCurrency = organizationCountry ? getCountryCurrency(organizationCountry) : undefined;

  const allowedModulrAccounts = data.accounts
    .filter(isBankingAccount)
    .filter(isModulrAccount)
    .filter((account) => account.currency === allowedCurrency);
  const hasAllowedModulrAccount = allowedModulrAccounts.length > 0;
  const hasCards = data.cards.length > 0;

  const { firstName, lastName } = parseUserName(authenticatedInfo?.name);

  const handleCreateCard = () => {
    setIsCreateDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsCreateDrawerOpen(false);
  };

  const handleCardCreated = () => {
    setIsCreateDrawerOpen(false);
  };

  const handleSelectCard = (cardId: string) => {
    setSelectedCardId(cardId);
  };

  const handleCloseDetailsDrawer = () => {
    setSelectedCardId(null);
  };

  const handleEditNickname = (card: CardView) => {
    setEditingCard(card);
  };

  const handleSaveNickname = async (displayName: string) => {
    if (editingCard) {
      await updateDisplayName(editingCard.id, displayName);
      setEditingCard(null);
    }
  };

  const handleCancelCard = (card: CardView) => {
    setCancellingCard(card);
  };

  const handleConfirmCancel = async () => {
    if (cancellingCard) {
      await cancelCard(cancellingCard.id);
      setCancellingCard(null);
    }
  };

  const getCardholderName = (card: CardView) => `${card.holderFirstName} ${card.holderLastName}`.trim();
  const getDefaultNickname = (card: CardView) => `${getCardholderName(card)}'s card`;
  const getCurrentDisplayName = (card: CardView) => card.displayName || getDefaultNickname(card);
  const getCardLast4 = (card: CardView) => card.maskedPan.slice(-4);

  return (
    <Stack align="stretch" className="min-h-screen py-2 pr-2">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-md border border-border bg-background shadow-xs">
        <Stack align="stretch" className="bg-card px-6 pt-4" space={5}>
          <h1 className="text-2xl font-semibold text-foreground">Cards</h1>

          {hasAllowedModulrAccount && hasCards && (
            <CardsTable
              cards={data.cards}
              onSelectCard={handleSelectCard}
              onEditNickname={handleEditNickname}
              onCancelCard={handleCancelCard}
              toolbarActions={
                <Button iconLeft={<Plus size={16} />} onClick={handleCreateCard}>
                  Create card
                </Button>
              }
            />
          )}

          {hasAllowedModulrAccount && !hasCards && <CardsEmptyState onCreateCard={handleCreateCard} />}

          {!hasAllowedModulrAccount && <MissingSeapointAccount requiredCurrency={allowedCurrency} />}
        </Stack>
      </Stack>

      {hasAllowedModulrAccount && (
        <CardCreationDrawer
          isOpen={isCreateDrawerOpen}
          onClose={handleCloseDrawer}
          onSuccess={handleCardCreated}
          onViewCard={(card) => setSelectedCardId(card.id)}
          accounts={allowedModulrAccounts}
          firstName={firstName}
          lastName={lastName}
        />
      )}

      {selectedCard && (
        <CardDetailsDrawer
          isOpen
          onClose={handleCloseDetailsDrawer}
          card={selectedCard}
          accounts={data.accounts}
          transactions={data.transactions}
        />
      )}

      {editingCard && (
        <EditCardNicknameDialog
          isOpen
          onClose={() => setEditingCard(null)}
          onSave={handleSaveNickname}
          currentDisplayName={getCurrentDisplayName(editingCard)}
          defaultNickname={getDefaultNickname(editingCard)}
          isLoading={isUpdating}
        />
      )}

      {cancellingCard && (
        <CancelCardDialog
          isOpen
          onClose={() => setCancellingCard(null)}
          onConfirm={handleConfirmCancel}
          cardLast4={getCardLast4(cancellingCard)}
          isLoading={isCancelling}
        />
      )}
    </Stack>
  );
};
